import { useMemo } from 'react'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { useTranslation } from 'react-i18next'
import type { HeatmapPoint } from '@/types/dashboard'

interface HeatmapGridProps {
  from: string
  to: string
  days: HeatmapPoint[]
  /** Компактный режим (виджет): фиксированные мелкие клетки */
  compact?: boolean
  /** Подписи месяцев и дней недели (полноразмерный режим страницы) */
  showLabels?: boolean
  /** Растянуть сетку на всю доступную ширину (клетки остаются квадратными) */
  fill?: boolean
  /** Легенда цветовой шкалы под графиком */
  showLegend?: boolean
  /** Клик по дню — открыть детализацию */
  onDayClick?: (date: string) => void
}

/** Прозрачность заливки по уровню 0–4 (0 — пустая клетка) */
const LEVEL_ALPHA = [0, 0.18, 0.4, 0.65, 1]

/** Строки с подписью дня недели (Пн / Ср / Пт), как в GitHub */
const LABELED_WEEKDAYS = [0, 2, 4]

/**
 * Понедельник недели, в которую попадает дата.
 *
 * Нельзя брать `startOf('week')`: он зависит от локали dayjs и в английской
 * неделя начинается с воскресенья — сетка съехала бы на день относительно
 * подписей дней недели при переключении языка. `day()` от локали не зависит
 * (0 — воскресенье), поэтому считаем сдвиг сами.
 */
function startOfMonday(date: Dayjs): Dayjs {
  const weekday = date.day()

  return date.subtract(weekday === 0 ? 6 : weekday - 1, 'day')
}

/** Минимальный зазор в колонках между подписями месяцев, чтобы они не наезжали */
const MIN_LABEL_GAP = 3

/**
 * GitHub-style тепловая карта активности: колонки — недели, строки — дни недели.
 * Рендерится на CSS-flex без сторонних chart-библиотек.
 */
export function HeatmapGrid({
  from,
  to,
  days,
  compact = false,
  showLabels = false,
  fill = false,
  showLegend = false,
  onDayClick,
}: HeatmapGridProps) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()

  // Локаль применяем точечно к форматированию подписей: глобальный
  // dayjs.locale() в теле рендера менял бы поведение dayjs во всём приложении.
  const locale = i18n.language?.startsWith('en') ? 'en' : 'ru'

  const { weeks, pointsByDate } = useMemo(() => {
    const pointsMap = new Map(days.map((point) => [point.date, point]))
    // Начало сетки — понедельник недели, в которую попадает from
    const start = startOfMonday(dayjs(from))
    const end = dayjs(to)

    const allWeeks: string[][] = []
    let cursor = start
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      const week: string[] = []
      for (let day = 0; day < 7; day++) {
        week.push(cursor.format('YYYY-MM-DD'))
        cursor = cursor.add(1, 'day')
      }
      allWeeks.push(week)
    }
    return { weeks: allWeeks, pointsByDate: pointsMap }
  }, [days, from, to])

  /**
   * Подпись месяца — над колонкой, где месяц сменился, но не ближе
   * MIN_LABEL_GAP колонок к предыдущей подписи: иначе первые месяцы наезжают
   * друг на друга (период начинается с середины месяца).
   */
  const monthLabels = useMemo(() => {
    if (!showLabels) return []
    let previousMonth = ''
    let lastLabelIndex = -MIN_LABEL_GAP
    return weeks.map((week, index) => {
      const month = dayjs(week[0]).locale(locale).format('MMM')
      if (month === previousMonth) return ''
      previousMonth = month
      if (index - lastLabelIndex < MIN_LABEL_GAP) return ''
      lastLabelIndex = index
      return month
    })
  }, [weeks, showLabels, locale])

  const cellSize = compact ? 8 : 11
  const gap = 2
  const labelWidth = showLabels ? 28 : 0

  // В fill-режиме ширину задаёт колонка, а квадратность — aspect-ratio клетки.
  // Никакого flex на самой клетке: в колонке он растянул бы её в прямоугольник.
  const cellSx = fill
    ? { width: '100%', aspectRatio: '1 / 1' }
    : { width: cellSize, height: cellSize }

  const columnSx = fill
    ? { flex: '1 1 0', minWidth: 0 }
    : { width: cellSize, flexShrink: 0 }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ overflowX: fill ? 'visible' : 'auto', pb: 0.5 }}>
        {showLabels && (
          <Box sx={{ display: 'flex', gap: `${gap}px`, pl: `${labelWidth + gap}px`, mb: 0.5 }}>
            {monthLabels.map((label, index) => (
              <Box key={index} sx={{ ...columnSx, position: 'relative', height: 16 }}>
                {label && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      whiteSpace: 'nowrap',
                      lineHeight: '16px',
                    }}
                  >
                    {label}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: `${gap}px` }}>
          {showLabels && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: `${gap}px`,
                width: labelWidth,
                flexShrink: 0,
              }}
            >
              {/*
                Высоту строк задаёт сетка недель, а не cellSx: в режиме fill
                клетка ~15px, и фиксированные 28px развалили бы выравнивание
              */}
              {Array.from({ length: 7 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    ...(fill ? { flex: '1 1 0' } : { height: cellSize }),
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 0,
                  }}
                >
                  {LABELED_WEEKDAYS.includes(index) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 10, lineHeight: 1 }}
                    >
                      {startOfMonday(dayjs()).add(index, 'day').locale(locale).format('dd')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: `${gap}px`,
              width: fill ? '100%' : 'max-content',
              minWidth: 0,
            }}
          >
            {weeks.map((week, weekIndex) => (
              <Box
                key={weekIndex}
                sx={{ display: 'flex', flexDirection: 'column', gap: `${gap}px`, ...columnSx }}
              >
                {week.map((date) => {
                  const point = pointsByDate.get(date)
                  const level = point?.level ?? 0
                  const inRange = date >= from && date <= to
                  const clickable = inRange && Boolean(onDayClick)
                  const backgroundColor = !inRange
                    ? 'transparent'
                    : level === 0
                      ? alpha(theme.palette.text.primary, 0.06)
                      : alpha(theme.palette.primary.main, LEVEL_ALPHA[Math.min(level, 4)])

                  const cell = (
                    <Box
                      onClick={clickable ? () => onDayClick?.(date) : undefined}
                      sx={{
                        ...cellSx,
                        borderRadius: '2px',
                        backgroundColor,
                        cursor: clickable ? 'pointer' : 'default',
                        outline: '1px solid transparent',
                        '&:hover': clickable
                          ? { outlineColor: theme.palette.primary.main }
                          : undefined,
                      }}
                    />
                  )

                  if (!inRange || !point) {
                    return <Box key={date}>{cell}</Box>
                  }

                  return (
                    <Tooltip
                      key={date}
                      title={t('dashboard.widgets.students_activity_heatmap.eventsOn', {
                        count: point.count,
                        date: dayjs(date).format('DD.MM.YYYY'),
                      })}
                      placement="top"
                      arrow
                    >
                      {cell}
                    </Tooltip>
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('activity.legendLess')}
          </Typography>
          {LEVEL_ALPHA.map((_, level) => (
            <Box
              key={level}
              sx={{
                width: 11,
                height: 11,
                borderRadius: '2px',
                backgroundColor:
                  level === 0
                    ? alpha(theme.palette.text.primary, 0.06)
                    : alpha(theme.palette.primary.main, LEVEL_ALPHA[level]),
              }}
            />
          ))}
          <Typography variant="caption" color="text.secondary">
            {t('activity.legendMore')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
